import React, { useState, useEffect } from 'react';
import { Box, TextField, Typography, Alert, Tooltip, InputAdornment, Link as MuiLink, FormControlLabel, Checkbox } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { useSignupWizard } from '../../../contexts/SignupWizardContext';
import { initiateSignup } from '../../../utils/signupApi';
import { en } from '../../../translations/en';

interface SignupStep1ContactProps {
  onNext: () => void;
  selectedTier?: 'viewer' | 'creator' | 'commercial' | null;
}

export const SignupStep1Contact: React.FC<SignupStep1ContactProps> = ({
  onNext,
  selectedTier = 'viewer',
}) => {
  const {
    state,
    setContactInfo,
    setSignupToken,
    setCognitoUsername,
    setError,
    setLoading,
    setSelectedTier,
  } = useSignupWizard();

  const [localEmail, setLocalEmail] = useState(state.email);
  const [localFirstName, setLocalFirstName] = useState(state.firstName);
  const [localLastName, setLocalLastName] = useState(state.lastName);
  const [localDisplayName, setLocalDisplayName] = useState(state.displayName || '');
  const [localInitials, setLocalInitials] = useState(state.initials || '');
  const [localOrganization, setLocalOrganization] = useState(state.organization || '');
  const [localError, setLocalError] = useState<string | null>(null);
  const [showExistingEmailInfo, setShowExistingEmailInfo] = useState(false);
  const [displayNameManuallyEdited, setDisplayNameManuallyEdited] = useState(!!state.displayName);
  const [initialsManuallyEdited, setInitialsManuallyEdited] = useState(false);
  const [tosConsented, setTosConsented] = useState(false);

  const isCommercial = selectedTier === 'commercial';
  const isCreator = selectedTier === 'creator';
  const isPaidTier = selectedTier === 'creator' || selectedTier === 'commercial';
  // Display Name is NOT collected on Step 1 for any plan:
  // - Creators: collected on ALI ID page (Step 4) per #908
  // - Viewers/Commercial: not needed per #906
  const showDisplayNameField = false;

  // Auto-suggest display name from first and last name.
  // Once the user manually edits the field, auto-suggestion stops to preserve their custom choice.
  useEffect(() => {
    if (!displayNameManuallyEdited && localFirstName && localLastName) {
      setLocalDisplayName(`${localFirstName} ${localLastName}`);
    }
  }, [localFirstName, localLastName, displayNameManuallyEdited]);

  // Auto-suggest initials from first and last name
  useEffect(() => {
    if (!initialsManuallyEdited && localFirstName && localLastName) {
      const suggestedInitials = `${localFirstName[0].toUpperCase()}${localLastName[0].toUpperCase()}`;
      setLocalInitials(suggestedInitials);
    }
  }, [localFirstName, localLastName, initialsManuallyEdited]);

  const validateForm = (): boolean => {
    if (!localEmail || !localFirstName || !localLastName) {
      setLocalError(en.signup.validation.requiredFields);
      return false;
    }

    // Validate display name only when the field is shown on this step
    if (showDisplayNameField) {
      if (!localDisplayName.trim()) {
        setLocalError(en.signup.validation.displayNameRequired);
        return false;
      }
      if (localDisplayName.trim().toLowerCase() === 'anon') {
        setLocalError(en.signup.validation.displayNameReserved);
        return false;
      }
      if (localDisplayName.trim().length > 100) {
        setLocalError(en.signup.validation.displayNameMaxLength);
        return false;
      }
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(localEmail)) {
      setLocalError('Please enter a valid email address');
      return false;
    }

    // Validate initials for paid tiers (creator/commercial)
    if (isPaidTier) {
      if (!localInitials || localInitials.length !== 2) {
        setLocalError('Initials must be exactly 2 letters');
        return false;
      }
      if (!/^[A-Za-z]{2}$/.test(localInitials)) {
        setLocalError('Initials must be letters only');
        return false;
      }
    }

    if (isCommercial && !localOrganization) {
      setLocalError(en.signup.validation.organizationRequired);
      return false;
    }

    if (!tosConsented) {
      setLocalError(en.signup.consent.required);
      return false;
    }

    setLocalError(null);
    setShowExistingEmailInfo(false);
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Update context with form values
      setContactInfo({
        email: localEmail,
        firstName: localFirstName,
        lastName: localLastName,
        displayName: localDisplayName.trim(),
        initials: localInitials.toUpperCase() || undefined,
        organization: localOrganization || undefined,
      });

      // Set the selected tier
      setSelectedTier(selectedTier);

      // Call the API to initiate signup
      const response = await initiateSignup({
        email: localEmail,
        firstName: localFirstName,
        lastName: localLastName,
        displayName: localDisplayName.trim(),
        initials: isPaidTier ? localInitials.toUpperCase() : undefined,
        organization: localOrganization || undefined,
        // TODO (Phase 4): pass versions from /api/legal/current once that endpoint exists (ACPT-08)
        // Backend ignores client-supplied versions and fetches from DynamoDB directly.
      });

      if (response.success && response.signupToken) {
        setSignupToken(response.signupToken);
        // Store cognitoUsername if using Cognito verification flow
        if (response.cognitoUsername) {
          setCognitoUsername(response.cognitoUsername);
        }
        onNext();
      } else if (response.success && !response.signupToken) {
        // Account enumeration protection: email may already exist
        setShowExistingEmailInfo(true);
        setLocalError(null);
      } else {
        setLocalError(response.error || response.message || 'Failed to initiate signup');
      }
    } catch (error) {
      const message = (error as Error).message || 'An unexpected error occurred';
      setLocalError(message);
    } finally {
      setLoading(false);
    }
  };

  // Expose submit handler for parent component
  React.useEffect(() => {
    // Store the submit handler on the window for the parent to call
    (window as unknown as { __signupStep1Submit: () => Promise<void> }).__signupStep1Submit =
      handleSubmit;
    return () => {
      delete (window as unknown as { __signupStep1Submit?: () => Promise<void> })
        .__signupStep1Submit;
    };
  });

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {en.signup.accountCreation.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {selectedTier === 'viewer'
          ? en.signup.accountCreation.viewerDescription
          : isCommercial
            ? en.signup.accountCreation.generalCatalogDescription
            : en.signup.accountCreation.creatorDescription}
      </Typography>

      {localError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {localError}
        </Alert>
      )}

      {showExistingEmailInfo && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="existing-email-info">
          {en.signup.existingEmail.infoMessage}{' '}
          <MuiLink
            component={RouterLink}
            to="/auth"
            sx={{ color: 'inherit', fontWeight: 'bold' }}
          >
            {en.signup.existingEmail.signInLink}
          </MuiLink>
          .
        </Alert>
      )}

      <TextField
        fullWidth
        label={en.signup.form.email}
        type="email"
        value={localEmail}
        onChange={(e) => {
          setLocalEmail(e.target.value);
          if (showExistingEmailInfo) setShowExistingEmailInfo(false);
        }}
        margin="normal"
        required
        autoComplete="email"
      />

      <TextField
        fullWidth
        label={en.signup.form.firstName}
        value={localFirstName}
        onChange={(e) => setLocalFirstName(e.target.value)}
        margin="normal"
        required
        autoComplete="given-name"
      />

      <TextField
        fullWidth
        label={en.signup.form.lastName}
        value={localLastName}
        onChange={(e) => setLocalLastName(e.target.value)}
        margin="normal"
        required
        autoComplete="family-name"
      />

      {showDisplayNameField && (
        <TextField
          fullWidth
          label={en.signup.form.displayName}
          value={localDisplayName}
          onChange={(e) => {
            setDisplayNameManuallyEdited(true);
            setLocalDisplayName(e.target.value);
          }}
          margin="normal"
          required
          inputProps={{ maxLength: 100 }}
          helperText={en.signup.form.displayNameHelper}
        />
      )}

      {isPaidTier && (
        <TextField
          fullWidth
          label="Initials"
          value={localInitials}
          onChange={(e) => {
            setInitialsManuallyEdited(true);
            setLocalInitials(e.target.value.toUpperCase().slice(0, 2));
          }}
          margin="normal"
          required
          inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
          helperText="Your 2-letter initials will be used in your ALI ID (e.g., JD → JD-3K7)"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Your ALI ID uniquely identifies you and your catalog items. Format: LL-NXX where LL is your initials.">
                  <InfoOutlinedIcon fontSize="small" color="action" />
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      )}

      {(isCommercial || selectedTier === 'viewer') && (
        <TextField
          fullWidth
          label={en.signup.form.organization}
          value={localOrganization}
          onChange={(e) => setLocalOrganization(e.target.value)}
          margin="normal"
          required={isCommercial}
          autoComplete="organization"
        />
      )}

      <FormControlLabel
        data-testid="tos-consent-checkbox"
        sx={{ mt: 2, alignItems: 'flex-start' }}
        control={
          <Checkbox
            checked={tosConsented}
            onChange={(e) => setTosConsented(e.target.checked)}
            sx={{ pt: 0 }}
          />
        }
        label={
          <Typography variant="body2">
            {en.signup.consent.label}{' '}
            <MuiLink component={RouterLink} to="/terms" target="_blank">
              {en.signup.consent.termsLink}
            </MuiLink>{' '}
            {en.signup.consent.and}{' '}
            <MuiLink component={RouterLink} to="/privacy" target="_blank">
              {en.signup.consent.privacyLink}
            </MuiLink>
          </Typography>
        }
      />
    </Box>
  );
};

export default SignupStep1Contact;
